import { Navigate, Route, Routes } from "react-router-dom";

import { ForgotPasswordView } from "@/features/auth/ForgotPasswordView";
import { LoginView } from "@/features/auth/LoginView";
import { ResetPasswordView } from "@/features/auth/ResetPasswordView";
import { SignupView } from "@/features/auth/SignupView";
import { LegalDocumentView } from "@/features/legal/LegalDocumentView";
import { AccountLayout } from "@/routes/AccountLayout";
import { AuthGuard, GuestOnly } from "@rook/shared/auth/AuthGuard";
import { AuthProvider, useAuth } from "@rook/shared/auth/AuthProvider";
import { CrossOriginHandoff } from "@rook/shared/components/CrossOriginHandoff";
import {
  PER_ENV_PORTAL_ORIGINS,
  type PortalOrigin,
} from "@rook/shared/lib/portalOrigins";

/**
 * `@rook/landing` App — the single-deploy front-door of the customer
 * portal under `clients(-tmp).portal.tryrook.io` (cuenta 3 / us-east-1).
 *
 * Responsibilities:
 *  1. Pre-auth UI: login, signup, forgot-password, reset-password,
 *     legal documents (public — no auth gate; counsel reads these).
 *  2. Post-auth redirect: a customer who lands here authenticated
 *     is bounced cross-origin to the env-portal they belong to.
 *     The overlay (`EnvSwitchOverlay`) renders during the ~200 ms
 *     redirect so there's never a blank tab — CPF-NFR-028.
 *
 * What's intentionally NOT here (lives in `@rook/portal`):
 *  - `/home/*` dashboard, logs, users, score, webhooks, etc.
 *  - The per-tenant per-env work the customer does day-to-day.
 *
 * How the env-portal target is chosen (entitlement-driven)
 * --------------------------------------------------------
 * "You logged in → which env do you go to" is now answered by the
 * customer's ORG entitlements (CPF-NFR-024), surfaced on the session
 * by the landing's auth-session (`session.isProductionActive`, read
 * from `cpf-owner-entitlements`). Prod-active customers land on
 * production; everyone else — including brand-new signups with no
 * entitlement row yet — lands on sandbox, the safe default. This
 * replaces the earlier "everyone → production" placeholder. Customers
 * with access to more than one env can still switch from inside
 * `/home` (CPF-NFR-028's `EnvSwitchButton`).
 */

const PRODUCTION_ORIGIN: PortalOrigin = PER_ENV_PORTAL_ORIGINS[0];
const SANDBOX_ORIGIN: PortalOrigin = PER_ENV_PORTAL_ORIGINS[1];

/**
 * Wrap a guest-only auth view in the visual shell (logo + 2-col layout)
 * AND the GuestOnly gate so a still-authenticated user gets bounced
 * to `/home` (which in turn cross-origin redirects to their env-portal).
 */
const wrapGuest = (children: React.ReactNode) => (
  <GuestOnly>
    <AccountLayout>{children}</AccountLayout>
  </GuestOnly>
);

/**
 * `/home` (and any portal-shaped path) doesn't exist in `@rook/landing`.
 * What lives here is the cross-origin handoff: render the branded
 * overlay (so the customer never sees a blank tab), then
 * `window.location.assign(<env-portal-origin>/home...)` so the destination's
 * shell paints first. CPF-NFR-028.
 *
 * The component is a thin `<CrossOriginHandoff>` wrapper — the
 * RAF-guarded paint-before-navigate primitive lives in
 * @rook/shared/components/CrossOriginHandoff.tsx and is shared with
 * @rook/portal's HandoffToLanding (same flow, opposite direction).
 * Deep links survive: a customer clicking
 * `clients.portal.tryrook.io/home/logs?user=abc` lands on the
 * env-portal at the same sub-path because `targetPath` is omitted.
 */
const PostLoginEnvHandoff = () => {
  const { session } = useAuth();
  // session.ok is guaranteed true here (AuthGuard gates this route),
  // but the explicit `=== true` also makes an absent flag → sandbox,
  // so a degraded/legacy session can never route INTO production.
  const isProd = session.ok && session.isProductionActive === true;
  return (
    <CrossOriginHandoff
      targetOrigin={isProd ? PRODUCTION_ORIGIN : SANDBOX_ORIGIN}
      targetEnvLabel={isProd ? "Production" : "Sandbox"}
    />
  );
};

export const App = () => (
  <AuthProvider>
    <Routes>
      <Route path="/" element={wrapGuest(<LoginView />)} />
      <Route path="/create-account" element={wrapGuest(<SignupView />)} />
      <Route
        path="/forgot-password"
        element={wrapGuest(<ForgotPasswordView />)}
      />
      <Route
        path="/forgot-password/reset"
        element={wrapGuest(<ResetPasswordView />)}
      />

      {/* Legal docs — public, no auth gate. Counsel + the customer's
          legal team need to read these without a session. */}
      <Route path="/legal" element={<LegalDocumentView />} />
      <Route path="/legal/:slug" element={<LegalDocumentView />} />

      {/* Post-login env-portal handoff (CPF-NFR-028). Any /home or
          deeper path here triggers a cross-origin redirect to the
          customer's assigned env-portal, behind the branded overlay
          so the page change is invisible. */}
      <Route
        path="/home/*"
        element={
          <AuthGuard>
            <PostLoginEnvHandoff />
          </AuthGuard>
        }
      />

      {/* Anything else — back to login. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </AuthProvider>
);
